import { useState, useEffect } from 'react';
import {
  IonSpinner, IonToast, IonIcon,
} from '@ionic/react';
import {
  checkmarkCircle, closeCircle, time, shieldCheckmark, refresh, create,
} from 'ionicons/icons';
import { useRequestVerification, useLatestVerification, useCandidateProfile, useMyCandidateProfile, useLegalEntity, useMyBeneficiaryOrders } from '@/lib/api/hooks';
import '../../pages/dashboard/VerifyIdentity.css';

const STATUS_CONFIG = {
  Verified: { icon: checkmarkCircle, heroClass: 'vi-hero--verified', label: 'Identity Verified' },
  Failed: { icon: closeCircle, heroClass: 'vi-hero--failed', label: 'Verification Failed' },
  ManualReview: { icon: time, heroClass: 'vi-hero--review', label: 'Under Review' },
  Pending: { icon: time, heroClass: 'vi-hero--pending', label: 'Pending Review' },
} as const;

interface VerifyIdentityPanelProps {
  onDone?: () => void;
}

export function VerifyIdentityPanel({ onDone }: VerifyIdentityPanelProps) {
  // Candidate profile id via /candidates/me — authStore profile.id is the userprofile id.
  const { data: myCandidateEnvelope } = useMyCandidateProfile();
  const candidateId = myCandidateEnvelope?.data?._id ?? '';

  const { data: verificationEnvelope, isLoading: loadingVerification } = useLatestVerification(candidateId);
  const verification = verificationEnvelope?.data;

  const requestVerification = useRequestVerification();

  const { data: candidateProfileEnvelope } = useCandidateProfile(candidateId);
  const legalEntityId = candidateProfileEnvelope?.data?.legalEntity?.id ?? '';
  const { data: legalEntityEnvelope } = useLegalEntity(legalEntityId);
  const nationalIds = legalEntityEnvelope?.data?.nationalIds ?? [];

  const [idNumber, setIdNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [idType, setIdType] = useState<'SA_ID' | 'PASSPORT'>('SA_ID');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);
  const [prePopulated, setPrePopulated] = useState(false);

  useEffect(() => {
    if (prePopulated || nationalIds.length === 0) return;
    const primary = nationalIds[0];
    if (primary?.number) {
      setIdNumber(primary.number);
      if (primary.type === 'Passport') setIdType('PASSPORT');
    }
    setPrePopulated(true);
  }, [nationalIds, prePopulated]);

  // Sponsored credit (M07 S06 T6.4): an open Paid order means someone already
  // paid for this verification — show the beneficiary banner, never sponsor PII.
  const { data: beneficiaryOrdersEnvelope } = useMyBeneficiaryOrders();
  const sponsoredCredit = (beneficiaryOrdersEnvelope?.data ?? []).find(
    (o) =>
      o.serviceKey === 'identity-verification' &&
      o.status === 'Paid' &&
      !o.fulfillmentRef,
  );
  const sponsorLabel = sponsoredCredit?.sponsorDisplayName || 'an anonymous sponsor';

  // Shareable sponsor link (T6.3): the candidate asks someone else to pay.
  const candidateSlug = myCandidateEnvelope?.data?.slug ?? '';
  const shareSponsorLink = async () => {
    const url = `${window.location.origin}/sponsor/${candidateSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ msg: 'Sponsor link copied', color: 'success' });
    } catch {
      setToast({ msg: url, color: 'medium' });
    }
  };

  const statusInfo = verification ? STATUS_CONFIG[verification.status] : null;

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!idNumber.trim()) newErrors.idNumber = 'ID number is required';
    else if (idType === 'SA_ID' && !/^\d{13}$/.test(idNumber)) newErrors.idNumber = 'SA ID must be 13 digits';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      await requestVerification.mutateAsync({
        candidateId,
        idNumber,
        fullName,
        idType,
      });
      setToast({ msg: 'Verification submitted', color: 'success' });
    } catch {
      setToast({ msg: 'Failed to submit verification', color: 'danger' });
    }
  };

  if (loadingVerification) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <IonSpinner name="crescent" />
      </div>
    );
  }

  return (
    <div className="vi-page">
      {sponsoredCredit && verification?.status !== 'Verified' && (
        <div
          role="status"
          aria-label="Sponsored verification"
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            padding: '12px 14px',
            marginBottom: '14px',
            borderRadius: '10px',
            background: 'color-mix(in srgb, var(--ion-color-success) 12%, transparent)',
            border: '1px solid var(--ion-color-success)',
            fontSize: '0.9rem',
          }}
        >
          <IonIcon icon={checkmarkCircle} style={{ color: 'var(--ion-color-success)' }} />
          <span>
            Your verification has been sponsored by {sponsorLabel} — complete it
            now.
          </span>
        </div>
      )}
      {verification && statusInfo ? (
        <>
          <div className={`vi-hero ${statusInfo.heroClass}`}>
            <div className="vi-hero-glow" />
            <div className="vi-hero-icon">
              <IonIcon icon={statusInfo.icon} />
            </div>
            <h2>{statusInfo.label}</h2>
            <div className="vi-detail-pill">
              <IonIcon icon={shieldCheckmark} />
              <span>{verification.provider}</span>
            </div>
            {verification.result.evidence && (
              <div className="vi-evidence" style={{ marginTop: '16px' }}>
                <div className="vi-evidence-label">Details</div>
                <p className="vi-evidence-text">{verification.result.evidence}</p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
            {verification.status === 'Verified' ? (
              <button className="vi-submit" onClick={onDone}>
                <IonIcon icon={checkmarkCircle} />
                Done
              </button>
            ) : verification.status === 'Failed' ? (
              <button className="vi-submit" onClick={() => window.location.reload()}>
                <IonIcon icon={refresh} />
                Try again
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="vi-hero">
            <div className="vi-hero-glow" />
            <div className="vi-hero-icon">
              <IonIcon icon={shieldCheckmark} />
            </div>
            <h2>Verify your identity</h2>
            <p>Confirm your South African ID to earn a verified badge on your profile.</p>
          </div>

          <div className="vi-form-panel" style={{ marginTop: '20px' }}>
            <div className="vi-form-head">
              <IonIcon icon={create} />
              <h3>Enter your details</h3>
            </div>

            <div className="vi-form-body">
              <div className="vi-field">
                <label className="vi-label">Full name (as on ID)</label>
                <input
                  className="vi-input"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. John Doe"
                />
                {errors.fullName && <span className="vi-error">{errors.fullName}</span>}
              </div>

              <div className="vi-field">
                <label className="vi-label">ID type</label>
                <select
                  className="vi-select"
                  value={idType}
                  onChange={(e) => setIdType(e.target.value as 'SA_ID' | 'PASSPORT')}
                >
                  <option value="SA_ID">South African ID</option>
                  <option value="PASSPORT">Passport</option>
                </select>
              </div>

              <div className="vi-field">
                <label className="vi-label">
                  {idType === 'SA_ID' ? 'SA ID number' : 'Passport number'}
                </label>
                <input
                  className="vi-input"
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder={idType === 'SA_ID' ? '0000000000000' : 'Passport number'}
                  maxLength={idType === 'SA_ID' ? 13 : 20}
                />
                {errors.idNumber && <span className="vi-error">{errors.idNumber}</span>}
              </div>

              <button
                className="vi-submit"
                onClick={handleSubmit}
                disabled={requestVerification.isPending}
              >
                {requestVerification.isPending ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    <IonIcon icon={shieldCheckmark} />
                    Submit verification
                  </>
                )}
              </button>
            </div>
          </div>

          {/* T6.3 secondary entry: someone else can pay for this verification. */}
          {candidateSlug && (
            <div style={{ marginTop: '14px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => void shareSponsorLink()}
                style={{
                  background: 'transparent',
                  border: '1px dashed var(--ion-color-medium)',
                  color: 'var(--ion-color-medium-shade)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Someone else can pay for this — copy sponsor link
              </button>
            </div>
          )}
        </>
      )}

      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2200} onDidDismiss={() => setToast(null)} />
    </div>
  );
}
